/**
 * ActivityPanel Component
 * Displays recent consumption activity and invoice processing history
 * Enhanced with clickable items for editing older invoices
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../ui/Icon';
import { NavIcon } from '../ui/NavIcon';
import { ActivityItem } from '@defs/api';
import { useCurrencySymbol } from '../../hooks/useCurrency';
import {
  formatServiceType,
  formatConsumption,
  formatRelativeTime,
} from '@utils/formatters';
import { formatComplianceTypeLabel } from '../../features/compliance/lib/labels';

interface ActivityPanelProps {
  activities: ActivityItem[];
  className?: string;
  enableNavigation?: boolean;
}

const Panel = styled.div`
  min-width: 0;
  padding: 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
`;

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItemContainer = styled.div<{ $clickable?: boolean }>`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.75rem 0; border-bottom: 1px solid var(--border-light); cursor: props=>props.$clickable?'pointer':'default'; &:hover { background: var(--interactive-hover); } &:focus-visible { outline: 2px solid var(--interactive-focus); outline-offset: 2px; }
`;

const ActivityContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const ActivityIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

const ActivityDetails = styled.div`
  flex: 1;
`;

const ActivityService = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const ActivityValue = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
`;

const ActivityTime = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  text-align: right;
  min-width: fit-content;
`;

const ClickableIndicator = styled.span`
  color: var(--brand-600);
  font-size: 0.75rem;
  margin-left: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ActivityItemContainer}:hover & {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
`;

const getActivityIcon = (serviceType: string): React.ReactNode => {
  if (serviceType === 'compliance-document') {
    return <NavIcon name="compliance" size="sm" aria-hidden />;
  }

  const iconMap: Record<string, string> = {
    electricidad: 'energy',
    agua: 'water',
    gas: 'gas',
    'residuos-reciclables': 'waste',
    'residuos-organicos': 'waste',
    'residuos-generales': 'waste',
    'covers-volumes': 'users',
    'supplier-operations': 'users',
  };
  const iconName = iconMap[serviceType] || 'file';
  return <Icon name={iconName as any} size="sm" aria-hidden />;
};

const isComplianceActivity = (activity: ActivityItem): boolean =>
  activity.activity_source === 'compliance' ||
  activity.service_type === 'compliance-document';

const formatActivityValue = (
  activity: ActivityItem,
  currencySymbol: string,
): string => {
  const consumption = formatConsumption(
    activity.consumption,
    activity.service_type,
  );
  if (activity.cost) {
    return `${consumption} • ${activity.cost}${currencySymbol}`;
  }
  return consumption;
};

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  activities,
  className,
  enableNavigation = true,
}) => {
  const currencySymbol = useCurrencySymbol();
  const navigate = useNavigate();

  const handleActivityClick = (activity: ActivityItem) => {
    if (!enableNavigation || !activity.id) return;

    let url: string;

    // Route based on activity ID pattern and source
    if (activity.id.startsWith('compliance_')) {
      const documentId = activity.id.replace('compliance_', '');
      url = `/compliance/${documentId}`;
    } else if (activity.id.startsWith('covers_volume_')) {
      // Extract numeric ID from covers_volume_X format
      const volumeId = activity.id.replace('covers_volume_', '');
      url = `/sales-results/${volumeId}`;
    } else if (activity.id.startsWith('supplier_')) {
      // Route supplier transactions to transactions list (no specific detail page needed)
      url = '/transactions';
    } else {
      // Regular service consumption transactions (OCR and manual)
      let transactionType: string | undefined;
      if (activity.activity_source === 'ocr_scan') {
        transactionType = 'ocr';
      } else if (activity.activity_source === 'manual_entry') {
        transactionType = 'manual';
      }

      // Clean up ID if it has manual_ prefix (for backward compatibility)
      const cleanId = activity.id.replace(/^manual_/, '');

      // Navigate with type parameter if available, otherwise let InvoiceResultsPage infer
      url = transactionType
        ? `/invoice-results?id=${cleanId}&type=${transactionType}`
        : `/invoice-results?id=${cleanId}`;
    }

    console.log(
      '🧭 [ActivityPanel] Navigating to:',
      url,
      'Source:',
      activity.activity_source,
      'ID:',
      activity.id,
    );
    navigate(url);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    activity: ActivityItem,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivityClick(activity);
    }
  };
  return (
    <Panel className={className}>
      <Header>
        <Title>Actividad Reciente</Title>
        <Subtitle>
          {enableNavigation
            ? 'Últimos consumos procesados • Haz clic para editar'
            : 'Últimos consumos procesados'}
        </Subtitle>
      </Header>

      {activities.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Icon name="chart" size="xl" aria-hidden />
          </EmptyIcon>
          <EmptyText>No hay actividad reciente</EmptyText>
          <EmptyText>Los nuevos escaneos aparecerán aquí</EmptyText>
        </EmptyState>
      ) : (
        <ActivityList>
          {activities.map((activity, index) => {
            const isClickable = enableNavigation && !!activity.id;
            const isCompliance = isComplianceActivity(activity);
            const complianceTypeLabel = activity.document_type
              ? formatComplianceTypeLabel(activity.document_type)
              : 'Documento';
            const activityValue = isCompliance
              ? complianceTypeLabel
              : formatActivityValue(activity, currencySymbol);

            return (
              <ActivityItemContainer
                key={activity.id || index}
                $clickable={isClickable}
                onClick={() => isClickable && handleActivityClick(activity)}
                onKeyDown={(e) => isClickable && handleKeyDown(e, activity)}
                tabIndex={isClickable ? 0 : -1}
                role={isClickable ? 'button' : undefined}
                aria-label={
                  isClickable
                    ? `Ver detalles de ${formatServiceType(activity.service_type)}`
                    : undefined
                }
              >
                <ActivityContent>
                  <ActivityIcon
                    role="img"
                    aria-label={`${activity.service_type} icon`}
                  >
                    {getActivityIcon(activity.service_type)}
                  </ActivityIcon>
                  <ActivityDetails>
                    <ActivityService>
                      {formatServiceType(activity.service_type)}
                    </ActivityService>
                    <ActivityValue>{activityValue}</ActivityValue>
                  </ActivityDetails>
                </ActivityContent>
                <ActivityTime>
                  {formatRelativeTime(
                    activity.created_at || activity.period_end,
                  )}
                  {isClickable && (
                    <ClickableIndicator>
                      <Icon name="edit" size="xs" aria-hidden />
                    </ClickableIndicator>
                  )}
                </ActivityTime>
              </ActivityItemContainer>
            );
          })}
        </ActivityList>
      )}
    </Panel>
  );
};

export default ActivityPanel;
